import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  useTheme,
  alpha,
  Stack,
  IconButton,
  Divider,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import { useFormikContext } from "formik";
import { BusinessFormValues } from "./BussinessAccounRegister";

const StepThreeReview: React.FC = () => {
  const { values } = useFormikContext<BusinessFormValues>();
  const theme = useTheme();
  const v = values;

  const [showPassword, setShowPassword] = useState(false);

  const GRADIENT = "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)";
  const PRIMARY = "#6366F1";
  const borderClr = alpha(PRIMARY, 0.25);

  const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({
    label,
    value,
  }) => (
    <Grid item xs={12} sm={6}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box mt="2px !important">
          {value ? (
            <CheckCircleRoundedIcon sx={{ fontSize: 20, color: "#22c55e" }} />
          ) : (
            <InfoOutlinedIcon
              sx={{ fontSize: 20, color: theme.palette.text.disabled }}
            />
          )}
        </Box>
        <Box>
          <Typography
            variant="caption"
            fontFamily='"Lexend", sans-serif'
            sx={{
              color: PRIMARY,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontWeight: 600,
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            fontFamily='"Lexend", sans-serif'
            sx={{ color: value ? theme.palette.text.primary : "#999" }}
          >
            {value || "Not provided"}
          </Typography>
        </Box>
      </Stack>
    </Grid>
  );

  const PasswordRow = () => (
    <Grid item xs={12} sm={6}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box mt="2px !important">
          {v.password ? (
            <CheckCircleRoundedIcon sx={{ fontSize: 20, color: "#22c55e" }} />
          ) : (
            <InfoOutlinedIcon
              sx={{ fontSize: 20, color: theme.palette.text.disabled }}
            />
          )}
        </Box>
        <Box flexGrow={1}>
          <Typography
            variant="caption"
            fontFamily='"Lexend", sans-serif'
            sx={{
              color: PRIMARY,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontWeight: 600,
            }}
          >
            Password
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body2"
              fontFamily='"Lexend", sans-serif'
              sx={{ color: v.password ? theme.palette.text.primary : "#999" }}
            >
              {showPassword ? v.password : v.password ? "••••••••" : "Not provided"}
            </Typography>
            {v.password && (
              <IconButton
                size="small"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <VisibilityOffRoundedIcon fontSize="small" />
                ) : (
                  <VisibilityRoundedIcon fontSize="small" />
                )}
              </IconButton>
            )}
          </Stack>
        </Box>
      </Stack>
    </Grid>
  );

  return (
    <Box>
      {/* Header */}
      <Typography
        variant="h6"
        mb={2}
        fontFamily='"Lexend", sans-serif'
        fontWeight={700}
        sx={{
          background: GRADIENT,
          WebkitBackgroundClip: "text",
          color: "transparent",
        }}
      >
        3️⃣ Review & Confirm Details
      </Typography>

      <Typography
        variant="body2"
        mb={4}
        fontFamily='"Lexend", sans-serif'
        color="text.secondary"
      >
        ✅ You're almost done! Please verify all details below before submitting.
      </Typography>

      {/* Review Card */}
      <Paper
        elevation={2}
        sx={{
          p: 4,
          borderRadius: theme.shape.borderRadius * 1.5,
          border: `1px solid ${borderClr}`,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            width: 6,
            background: GRADIENT,
            animation: "pulse 3s infinite",
          },
          "@keyframes pulse": {
            "0%": { opacity: 0.4 },
            "50%": { opacity: 1 },
            "100%": { opacity: 0.4 },
          },
        }}
      >
        {/* Group 1: Business Info */}
        <Typography
          variant="subtitle2"
          fontFamily='"Lexend", sans-serif'
          fontWeight={700}
          sx={{ mb: 2, color: PRIMARY }}
        >
          Business Information
        </Typography>
        <Grid container spacing={3} sx={{ pl: 1 }}>
          <Row label="Business Type" value={v.businessType} />
          <Row label="Business Name" value={v.businessName} />
          <Row label="Country" value={v.country} />
          <Row label="Address" value={v.address} />
          <Row label="Registration No." value={v.registrationNumber} />
          <Row label="Email Domain" value={v.emailDomain} />
          <PasswordRow />
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Group 2: Contact Info */}
        <Typography
          variant="subtitle2"
          fontFamily='"Lexend", sans-serif'
          fontWeight={700}
          sx={{ mb: 2, color: PRIMARY }}
        >
          Contact Details
        </Typography>
        <Grid container spacing={3} sx={{ pl: 1 }}>
          <Row label="Contact Name" value={v.contactName} />
          <Row
            label="Contact Phone"
            value={
              v.contactPhone
                ? `${v.contactCountryCode || ""} ${v.contactPhone}`
                : undefined
            }
          />
          <Row label="Business Email" value={v.businessEmail} />
        </Grid>
      </Paper>

      {/* Footer Microcopy */}
      <Typography
        variant="caption"
        fontFamily='"Lexend", sans-serif'
        sx={{ display: "block", textAlign: "center", mt: 3, color: "#666" }}
      >
        Need changes? Use the <strong>Back</strong> button. Your data is
        encrypted and secure.
      </Typography>
    </Box>
  );
};

export default StepThreeReview;
